import { useEffect, useState } from 'react';
import { InventoryAsset, SupportCasesItem } from '../../../types';
import {
  getNewCaseCreatedSubject,
  getNewCaseProcessingSubject,
  getCaseLoadingSubject,
  getCasesByAssetId,
  getCaseSubject,
  newCaseCreatedSubject,
  useSupportCases
} from '../../../store/SupportCase';
import { PermissibleRender } from '../../Compounds/PermissibleRender';
import { SupportCasesCard } from '../../Compounds/SupportCasesCard/SupportCasesCard';
import { SupportCaseWizard } from '../../Compounds/Wizards/SupportCaseWizard';
import { Permission } from '../../../store/GeneralStore';

export interface CaseDetailsDisplayProps {
  data: InventoryAsset;
  onCasesChanged?: () => Promise<void>;
  disabled?: boolean;
  disabledMessage?: string;
}

export function CaseDetailsDisplay({
  data,
  onCasesChanged,
  disabled = false,
  disabledMessage = ''
}: CaseDetailsDisplayProps) {
  const [casesLoading, setCasesLoading] = useState<boolean>(false);
  const [casesProcessing, setCasesProcessing] = useState<boolean>(false);
  const [newCaseCreated, setNewCaseCreated] = useState<boolean>(false);

  const [casesCollapsed, setCasesCollapsed] = useState<boolean>(true);
  const [cases, setCases] = useState<SupportCasesItem[]>([]);
  const { fetchCases } = useSupportCases();

  const orderCasesByDate = (cases: SupportCasesItem[]) => {
    return cases.sort((a, b) => {
      const dateA = new Date(a.dateTimeOpened);
      const dateB = new Date(b.dateTimeOpened);
      return dateB.getTime() - dateA.getTime();
    });
  };

  useEffect(() => {
    const caseLoadingSub = getCaseLoadingSubject().subscribe((state) => setCasesLoading(state));
    const caseSub = getCaseSubject().subscribe(() => setCases(orderCasesByDate(getCasesByAssetId(data.id))));
    const caseProcessingSub = getNewCaseProcessingSubject().subscribe((p) => setCasesProcessing(p));
    const newCaseSub = getNewCaseCreatedSubject().subscribe((n) => setNewCaseCreated(n));

    fetchCases();

    return () => {
      if (caseLoadingSub) caseLoadingSub.unsubscribe();
      if (caseSub) caseSub.unsubscribe();
      if (caseProcessingSub) caseProcessingSub.unsubscribe();
      if (newCaseSub) newCaseSub.unsubscribe();
      setCasesCollapsed(true);
      newCaseCreatedSubject.next(false);
    };
  }, []);

  useEffect(() => {
    setCases(orderCasesByDate(getCasesByAssetId(data.id)));
  }, [data?.id]);

  useEffect(() => {
    if (newCaseCreated) setCasesCollapsed(false);
  }, [newCaseCreated]);

  const cardActions = () => (
    <PermissibleRender requiredPermissions={[Permission.CREATE_CASES_FROM_INVENTORY]}>
      <SupportCaseWizard
        ctaClassName="text-grey-12 text-sm"
        inventoryAsset={data}
        onFormCloseHandler={async () => casesChanged()}
        disabled={disabled}
        disabledMessage={disabledMessage}
      />
    </PermissibleRender>
  );

  const casesChanged = async () => {
    fetchCases(true);
    if (onCasesChanged) {
      await onCasesChanged();
    }
  };

  return (
    <SupportCasesCard
      title={`Support Cases (${cases?.length || 0})`}
      cases={cases}
      emptyText="No Cases"
      loading={casesLoading}
      processing={casesProcessing}
      disabledMessage={disabled ? disabledMessage : undefined}
      cardActions={cardActions()}
      enableCollapse
      collapsed={casesCollapsed}
    />
  );
}
